import {FC, useState} from 'react'
import {IProductModel, PRODUCT_ACTIONS} from '../cors/_models'
import ProductTableRow from './ProductTableRow'
import {productAction, updateProduct} from '../cors/_requests'
import SharedData from '../../common/SharedData'
import AlertMessage from '../../common/alert/AlertMsg'
import {useIntl} from 'react-intl'
import NormalMessage from '../../common/NormalMessage'
import {isShopUser, shelfErrorOf, shopLabel, shopOf} from '../../common/stockScope'
import '../../../stylesheet/product-list/ProductsTable.css'

interface IProps {
  products: IProductModel[]
  refreshData: () => Promise<void>
}
const ProductsTable: FC<IProps> = ({products, refreshData}) => {
  const {formatMessage} = useIntl()
  const {currentUser, language} = SharedData()
  const [alert, setAlert] = useState({
    message: '',
    title: '',
    status: false,
  })

  const setMsg = (message: string, title: string, status: boolean = true) => {
    setAlert({
      message,
      title,
      status,
    })
  }

  const shopUser = isShopUser(currentUser)
  const shop = shopOf(currentUser)

  const changeQuantity = async (value: string, productID: number) => {
    try {
      await updateProduct(
        {
          supplier_quantity: value,
          product_id: productID,
        },
        currentUser?.token,
        language
      )

      setMsg(
        shop
          ? formatMessage({id: 'shop_scope.shelf_saved'}, {store: shopLabel(shop)})
          : formatMessage({ id: 'success_general_desc' }),
        formatMessage({ id: 'success_general_title' })
      )
      refreshData()
    } catch (err) {
      const refused = shelfErrorOf(err)
      setMsg(
        refused
          ? formatMessage({id: `shop_scope.err.${refused.error}`, defaultMessage: refused.message}, {held: refused.held})
          : formatMessage({ id: 'something_wrong_desc' }),
        formatMessage({ id: 'something_wrong_title' }),
        false
      )
      refreshData()
    }
  }

  const doProductAction = async (action: PRODUCT_ACTIONS, productID: number): Promise<any> => {
    try {
      await productAction(
        {
          product_id: productID,
        },
        action,
        currentUser?.token,
        language
      )
      refreshData()
    } catch {
      return
    }
  }

  return (
    <>
      {/* begin::Table container */}
      {products.length > 0 ? (
        <div className='table-responsive'>
        <table className='hsb-pl-table'>
          <thead>
          <tr>
            <th>{formatMessage({id: 'image'})}</th>
            <th>{formatMessage({id: 'prod_name'})}</th>
            <th>{formatMessage({id: 'barcode'})}</th>
            <th>{formatMessage({id: 'model_num'})}</th>
            <th>
              {shop
                ? formatMessage({id: 'shop_scope.shelf_at'}, {store: shopLabel(shop)})
                : formatMessage({id: 'qty'})}
            </th>
            {/* A shop's stock is its shelf; Dumyah's FC count is not this account's to manage. */}
            {!shopUser && <th>{formatMessage({id: 'warehouse_qty'})}</th>}
            <th>
              {formatMessage({id: 'sale_price'})}
            </th>
            <th>{formatMessage({id: 'brand'})}</th>
            <th>{`${formatMessage({id: 'status'})} - ${formatMessage({id: 'stock_status'})}`}</th>
            <th>{formatMessage({id: 'performance_col'})}</th>
            <th>{formatMessage({id: 'actions'})}</th>
          </tr>
          </thead>

          <tbody>
          {products &&
            products.map((product) => (
              <ProductTableRow
                onAction={doProductAction}
                onChangeQty={changeQuantity}
                key={product.product_id}
                product={product}
                shopUser={shopUser}
              />
            ))}
          </tbody>
        </table>
        </div>
      ) : (
        <NormalMessage msg={formatMessage({id: 'no_data'})} />
      )}
      {/* end::Table container */}

      {alert.message && (
        <AlertMessage
          onClose={() => setAlert({title: '', message: '', status: false})}
          message={alert.message}
          title={alert.title}
          status={alert.status}
        />
      )}
    </>
  );
};

export default ProductsTable;
